import {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_UPLOAD_TYPES,
  MAX_FILE_SIZE,
} from '@/config/constants';
import i18n from '@/lib/i18n';

/**
 * Client-side gate for a guided-entry motif upload.
 *
 * The server validates too; this exists so the seller gets a specific reason
 * instead of a generic upload failure, and so an oversized file never leaves
 * the browser.
 */

export type MotifRejection = 'empty' | 'format' | 'format-embroidery' | 'size';

export interface MotifCandidate {
  type: string;
  size: number;
}

/**
 * Returns why the file cannot be used, or null when it can.
 *
 * SVG and PDF are allowed since the assets API converts both to PNG on
 * arrival, so the stored motif -- and everything the mockup renderer and
 * the print file are built from -- is a bitmap.
 *
 * The embroidery run is stricter: its own endpoint takes only PNG, JPEG,
 * WebP and TIFF, so letting a document through here would pass the gate
 * and fail at the server.
 */
export function rejectMotif(
  file: MotifCandidate,
  options: { embroidery?: boolean } = {}
): MotifRejection | null {
  if (file.size <= 0) return 'empty';

  const type = file.type.toLowerCase();
  const allowed = (
    options.embroidery ? ALLOWED_IMAGE_TYPES : ALLOWED_UPLOAD_TYPES
  ) as readonly string[];
  if (!allowed.includes(type)) {
    // A file the print run would take, refused only because this is the
    // embroidery run, deserves to be told that rather than "wrong format".
    const printWouldTake = (ALLOWED_UPLOAD_TYPES as readonly string[]).includes(
      type
    );
    return options.embroidery && printWouldTake
      ? 'format-embroidery'
      : 'format';
  }

  if (file.size > MAX_FILE_SIZE) return 'size';

  return null;
}

/** Human-readable reason, in the seller's language. */
export function motifRejectionMessage(reason: MotifRejection): string {
  switch (reason) {
    case 'empty':
      return i18n.t('creator.guided.upload.empty');
    case 'format':
      return i18n.t('creator.guided.upload.format');
    case 'format-embroidery':
      return i18n.t('creator.guided.upload.formatEmbroidery');
    case 'size':
      return i18n.t('creator.guided.upload.tooLarge', {
        max: Math.round(MAX_FILE_SIZE / 1024 / 1024),
      });
  }
}
